import React, { useEffect, useMemo, useState } from 'react';
import FormControl from '../Form/FormControl';
import TextArea from '../Base/TextArea/TextArea';
import SubmitButton from '../Button/SubmitButton';
import TextField from '../Base/TextField/TextField';
import Checkbox from '../Base/Checkbox/Checkbox';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWrapper from '../Form/FormWrapper';
import SavingSpinner from '../Base/Loading/SavingSpinner';
import useFormDraft from '../Form/FormDraftState/useFormDraft';
import { FormDraftStateKey } from '../Form/FormDraftState/FormDraftStateKey';
import FormMultiplierRemoveButton from '../Form/FormMultiplierRemoveButton';
import RemoveButton from '../Button/RemoveButton';
import FormMultiplier from '../Form/FormMultiplier';
import { useModalContext } from '../../Modules/Modal/Services/ModalContext';
import { remapDiagnosisValues } from '../../Modules/MedicalReport/Services/medicalReport.service';
import usePatientConsent from '../PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../Modules/Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import useElectronicSignature from '../../Modules/Certificates/Hooks/useElectronicSignature';
import Loading from '../Loading';
import useScope from '../../Modules/Scope/Hooks/useScope';
import MedicalReportHungaryFields from '../../Modules/MedicalReport/Components/MedicalReportHungaryFields';
import useMedicalReportValidation from '../../Modules/MedicalReport/Hooks/useMedicalReportValidation';
import { Position } from '../../Modules/Base/Enums/Position.enum';
import ElectronicSignatureFields from '../../Modules/Base/Components/ElectronicSignatureFields/ElectronicSignatureFields';
import ElectronicSignaturePasswordRequestModal from '../../Modules/Certificates/Components/ElectronicSignaturePasswordRequest/ElectronicSignaturePasswordRequestModal';
import { SelectValue } from '../Base/Select/Select';
import { useAppContext } from '../../Services/AppContext';
import DiagnosisSelectField from '../Base/Diagnosis/DiagnosisSelectField';
import DiscourseTextLoadButton from '../../Modules/Discourse/Components/DiscourseTextLoadButton/DiscourseTextLoadButton';
import { useDisclose } from '../../Modules/Modal/Hooks/useDisclose';

interface MedicalReportDetailDefaultFormProps {
	onSubmit: (values: MedicalReportDetailDefaultFormValues) => void;
	defaultData?: any;
	isMeddiCase?: boolean;
	patientId: string;
}

interface MedicalReportDetailDefaultFormValues {
	title: string;
	diagnoses: SelectValue[];
	text: string;
	addToPatientECard?: boolean;
	sendToPatient?: boolean;
	contactSpecialist?: boolean;
	electronicSignature?: boolean;
	electronicSignatureTimestamp?: boolean;
}

const MedicalReportDetailDefaultForm: React.FC<
	MedicalReportDetailDefaultFormProps
> = ({ onSubmit, defaultData, isMeddiCase, patientId }) => {
	const defaultValues = useMemo(() => {
		return {
			...defaultData,
			addToPatientECard: isMeddiCase ?? defaultData?.addToPatientECard,
		};
	}, [defaultData, isMeddiCase]);

	const { isConsentProvided } = usePatientConsent(patientId);
	const { setModalProperties } = useModalContext();
	const [isLoading, setIsLoading] = useState(false);
	const {
		isOpen: isPasswordRequestOpen,
		onOpen: onPasswordRequestOpen,
		onClose: onPasswordRequestClose,
	} = useDisclose();
	const [formValues, setFormValues] = useState<
		MedicalReportDetailDefaultFormValues | undefined
	>();
	const {
		isPasswordRequiredForElectronicSignatureUsed,
		isLoading: electronicSignatureLoading,
	} = useElectronicSignature();
	const { isHungary, isUnicare } = useScope();
	const { validationSchema } = useMedicalReportValidation();
	const { globalTemplate } = useAppContext();

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		formMethods.reset(defaultValues);
	}, [defaultValues]);

	const { resetFormDraft, formDraftIsSaving } = useFormDraft({
		formMethods,
		key: `${FormDraftStateKey.MEDICAL_REPORT}_${patientId}`,
		defaultValues,
	});

	const handleSubmit = async (values: MedicalReportDetailDefaultFormValues) => {
		setIsLoading(true);
		resetFormDraft();
		values.diagnoses = remapDiagnosisValues(values?.diagnoses);
		onSubmit(values);
		setIsLoading(false);
	};

	const handleSave = async (values: MedicalReportDetailDefaultFormValues) => {
		if (
			isPasswordRequiredForElectronicSignatureUsed &&
			values.electronicSignature
		) {
			setFormValues(values);
			onPasswordRequestOpen();
			return;
		}
		await handleSubmit(values);
	};

	const handleOnVerify = async () => {
		onPasswordRequestClose();
		if (!formValues) return;
		await handleSubmit(formValues);
	};

	const handleOnStartOver = () => {
		resetFormDraft();
		formMethods.reset(defaultValues || {});
	};

	useEffect(() => {
		setModalProperties({
			onModalClose: () => resetFormDraft(),
		});
	}, []);

	if (electronicSignatureLoading) {
		return <Loading />;
	}

	return (
		<>
			<FormWrapper formMethods={formMethods}>
				<FormControl
					label='component.medicalReportCzechDefault.nameOfDocument'
					name='title'
					isRequired>
					<TextField />
				</FormControl>
				{isHungary && <MedicalReportHungaryFields />}
				<FormMultiplier
					name='diagnoses'
					addButtonTitle='base.addAnother'
					createDefault={0}>
					{({ index, itemsCount }) => (
						<>
							<DiagnosisSelectField
								label='component.medicalReportCzechDefault.diagnosis'
								name='diagnose'
								isRequired
							/>
							{itemsCount > 1 && index !== 0 && (
								<FormMultiplierRemoveButton>
									<RemoveButton
										onClick={() => void 0}
										title='base.shared.remove'
									/>
								</FormMultiplierRemoveButton>
							)}
						</>
					)}
				</FormMultiplier>
				<FormControl
					label='component.medicalReportCzechDefault.text'
					name='text'
					isRequired>
					<TextArea captureAudio={{ allow: true, position: Position.RIGHT }} />
				</FormControl>
				{isConsentProvided ? (
					<>
						<div className='mt-4'>
							<FormControl name='addToPatientECard' hideRequired>
								<Checkbox tx='base.addToPatientECard' disabled={isMeddiCase} />
							</FormControl>
						</div>
						{isUnicare && (
							<div className='mt-2'>
								<FormControl name='sendToPatient' hideRequired>
									<Checkbox tx='component.medicalReport.sendToPatient' />
								</FormControl>
							</div>
						)}
					</>
				) : (
					<PatientNoConsentProvided patientId={patientId} />
				)}
				<DiscourseTextLoadButton patientId={patientId} />
				<div className='mt-3'>
					<ElectronicSignatureFields />
				</div>
				{isMeddiCase && globalTemplate === 'default' && (
					<div className='mt-4'>
						<FormControl name='contactSpecialist' hideRequired={true}>
							<Checkbox tx='base.contactSpecialistText' />
						</FormControl>
					</div>
				)}
				<div className='d-flex mt-4 justify-content-between'>
					<SubmitButton
						title='base.shared.save'
						onSubmit={handleSave}
						disabled={isLoading}
					/>
					{formDraftIsSaving && <SavingSpinner />}
				</div>
			</FormWrapper>
			{isPasswordRequestOpen && (
				<ElectronicSignaturePasswordRequestModal
					isOpen
					onClose={onPasswordRequestClose}
					onVerify={handleOnVerify}
					submitButtonText='base.sign'
				/>
			)}
		</>
	);
};

export default MedicalReportDetailDefaultForm;
